import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, FileText, UtensilsCrossed, TrendingUp, Search, Plus, Key, X } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modal de Alteração de Senha
const ChangePasswordModal = ({ isOpen, onClose, userId, userName, onSuccess }) => {
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (novaSenha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (novaSenha !== confirmarSenha) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/change-password`, {
        nova_senha: novaSenha
      });
      toast.success(`Senha de ${userName} alterada com sucesso!`);
      setNovaSenha('');
      setConfirmarSenha('');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors"
        >
          <X size={20} className="text-muted-foreground" />
        </button>
        
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-[#B8977A]/20 flex items-center justify-center">
            <Key className="text-[#B8977A]" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-highlight">Alterar Senha</h3>
            <p className="text-sm text-muted-foreground">{userName}</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Nova Senha</label>
            <input
              data-testid="modal-nova-senha"
              type="password"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
              placeholder="Mínimo 6 caracteres"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Confirmar Nova Senha</label>
            <input
              data-testid="modal-confirmar-senha"
              type="password"
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
              placeholder="Digite novamente"
              required
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#B8977A] to-[#9C7D5E] text-white rounded-xl py-3 font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Alterar Senha'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-muted/50 text-muted-foreground rounded-xl py-3 font-semibold hover:bg-muted/70 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('patients'); // patients or doctors
  const [showCadastroForm, setShowCadastroForm] = useState(false);
  const [cadastroData, setCadastroData] = useState({
    nome: '',
    email: '',
    senha: '',
    telefone: '',
    data_nascimento: '',
    altura: '',
    peso_inicial: '',
    peso_meta: '',
    duracao_tratamento: '',
    historico_medico: '',
    role: 'patient',
    foto_perfil: '',
    // Medidas iniciais
    cintura_inicial: '',
    quadril_inicial: '',
    peito_inicial: '',
    braco_inicial: '',
    perna_inicial: ''
  });
  const [previewFoto, setPreviewFoto] = useState(null);
  const [passwordModal, setPasswordModal] = useState({ isOpen: false, userId: null, userName: '' });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [patientsRes, doctorsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/patients`),
        axios.get(`${API_URL}/api/admin/doctors`)
      ]);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('A foto deve ter no máximo 5MB');
        return;
      }

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione uma imagem válida');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setCadastroData({ ...cadastroData, foto_perfil: reader.result });
        setPreviewFoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCadastrarPaciente = async (e) => {
    e.preventDefault();
    try {
      // Preparar dados do paciente
      const data = {
        ...cadastroData,
        altura: cadastroData.altura ? parseFloat(cadastroData.altura) : null,
        peso_inicial: cadastroData.peso_inicial ? parseFloat(cadastroData.peso_inicial) : null,
        peso_meta: cadastroData.peso_meta ? parseFloat(cadastroData.peso_meta) : null,
        duracao_tratamento: cadastroData.duracao_tratamento ? parseInt(cadastroData.duracao_tratamento) : null,
        // Medidas iniciais
        cintura_inicial: cadastroData.cintura_inicial ? parseFloat(cadastroData.cintura_inicial) : null,
        quadril_inicial: cadastroData.quadril_inicial ? parseFloat(cadastroData.quadril_inicial) : null,
        peito_inicial: cadastroData.peito_inicial ? parseFloat(cadastroData.peito_inicial) : null,
        braco_inicial: cadastroData.braco_inicial ? parseFloat(cadastroData.braco_inicial) : null,
        perna_inicial: cadastroData.perna_inicial ? parseFloat(cadastroData.perna_inicial) : null
      };
      
      await axios.post(`${API_URL}/api/auth/register`, data);
      toast.success('Paciente cadastrado com sucesso!');
      setShowCadastroForm(false);
      setCadastroData({
        nome: '',
        email: '',
        senha: '',
        telefone: '',
        data_nascimento: '',
        altura: '',
        peso_inicial: '',
        peso_meta: '',
        duracao_tratamento: '',
        historico_medico: '',
        role: 'patient',
        foto_perfil: '',
        cintura_inicial: '',
        quadril_inicial: '',
        peito_inicial: '',
        braco_inicial: '',
        perna_inicial: ''
      });
      setPreviewFoto(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao cadastrar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar paciente');
    }
  };

  const handleFinalizarPrograma = async (patientId) => {
    if (!window.confirm('Deseja finalizar o programa deste paciente? Esta ação marcará o paciente como tendo concluído o programa.')) {
      return;
    }

    try {
      await axios.put(`${API_URL}/api/admin/patients/${patientId}/finalizar`);
      toast.success('Programa finalizado com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao finalizar programa:', error);
      toast.error('Erro ao finalizar programa');
    }
  };

  const handleExcluirPaciente = async (patientId, patientNome) => {
    if (!window.confirm(`Tem certeza que deseja EXCLUIR o paciente "${patientNome}"?\n\nEsta ação NÃO pode ser desfeita e todos os dados serão permanentemente removidos:\n- Histórico de progresso\n- Prescrições\n- Planos alimentares\n- Mensagens\n\nDigite SIM para confirmar.`)) {
      return;
    }

    const confirmacao = window.prompt('Digite "SIM" em letras maiúsculas para confirmar a exclusão:');
    if (confirmacao !== 'SIM') {
      toast.error('Exclusão cancelada');
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/admin/patients/${patientId}`);
      toast.success('Paciente excluído com sucesso');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir paciente:', error);
      toast.error('Erro ao excluir paciente');
    }
  };

  const filteredPatients = patients.filter(p => 
    p.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDoctors = doctors.filter(d => 
    d.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Painel Administrativo
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Gerencie pacientes e médicos do sistema
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <div className="floating-card p-4 sm:p-6 float-transition">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <Users className="text-[#B8977A]" size={18} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground font-medium">Pacientes</p>
                <p className="text-xl sm:text-3xl font-bold text-highlight">
                  {patients.length}
                </p>
              </div>
            </div>
          </div>

          <div className="floating-card p-4 sm:p-6 float-transition">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#0F2B4C]/20 to-[#0F2B4C]/5 flex items-center justify-center">
                <FileText className="text-[#0F2B4C]" size={18} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground font-medium">Médicos</p>
                <p className="text-xl sm:text-3xl font-bold text-highlight">
                  {doctors.length}
                </p>
              </div>
            </div>
          </div>

          <div className="floating-card p-4 sm:p-6 float-transition">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-[#B8977A]/20 to-[#B8977A]/5 flex items-center justify-center">
                <TrendingUp className="text-[#B8977A]" size={18} />
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground font-medium">Total</p>
                <p className="text-xl sm:text-3xl font-bold text-highlight">
                  {patients.length + doctors.length + 1}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs and Cadastro Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex gap-3">
            <button
              data-testid="tab-patients"
              onClick={() => setSelectedTab('patients')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold transition-all text-xs sm:text-base ${
                selectedTab === 'patients'
                  ? 'bg-gradient-to-r from-[#B8977A] to-[#9C7D5E] text-white shadow-lg shadow-[#B8977A]/30'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
              }`}
            >
              <Users className="inline mr-1 sm:mr-2" size={16} />
              <span className="hidden sm:inline">Pacientes </span>({patients.length})
            </button>
            <button
              data-testid="tab-doctors"
              onClick={() => setSelectedTab('doctors')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold transition-all text-xs sm:text-base ${
                selectedTab === 'doctors'
                  ? 'bg-gradient-to-r from-[#B8977A] to-[#9C7D5E] text-white shadow-lg shadow-[#B8977A]/30'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted/70'
              }`}
            >
              <FileText className="inline mr-1 sm:mr-2" size={16} />
              <span className="hidden sm:inline">Médicos </span>({doctors.length})
            </button>
          </div>

          {selectedTab === 'patients' && (
            <button
              data-testid="cadastrar-paciente-button"
              onClick={() => setShowCadastroForm(!showCadastroForm)}
              className="bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl px-5 sm:px-6 py-2.5 sm:py-3 font-semibold shadow-lg shadow-[#0F2B4C]/30 hover:shadow-xl hover:shadow-[#0F2B4C]/40 transition-all flex items-center gap-2 text-xs sm:text-base w-full sm:w-auto justify-center"
            >
              <Plus size={18} />
              <span className="sm:hidden">Cadastrar</span>
              <span className="hidden sm:inline">Cadastrar Paciente</span>
            </button>
          )}
        </div>

        {/* Formulário de Cadastro */}
        {showCadastroForm && selectedTab === 'patients' && (
          <div className="floating-card p-5 sm:p-6">
            <h3 className="text-base sm:text-xl font-bold mb-4 text-highlight">
              Cadastrar Novo Paciente
            </h3>
            <form onSubmit={handleCadastrarPaciente} className="space-y-4">
              {/* Foto de Perfil */}
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-5 bg-muted/50 rounded-xl">
                <div className="relative">
                  {previewFoto ? (
                    <img 
                      src={previewFoto} 
                      alt="Preview" 
                      className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-[#B8977A] shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-[#B8977A] to-[#0F2B4C] flex items-center justify-center shadow-lg">
                      <svg className="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                  )}
                  <label 
                    htmlFor="foto-upload" 
                    className="absolute bottom-0 right-0 bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl p-2 cursor-pointer hover:shadow-lg transition-all"
                    title="Escolher foto"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </label>
                  <input
                    id="foto-upload"
                    data-testid="cadastro-foto"
                    type="file"
                    accept="image/*"
                    onChange={handleFotoChange}
                    className="hidden"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-highlight mb-1">Foto de Perfil</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Adicione uma foto do paciente (opcional)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Formatos aceitos: JPG, PNG, GIF • Tamanho máximo: 5MB
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome Completo *</label>
                  <input
                    data-testid="cadastro-nome"
                    type="text"
                    value={cadastroData.nome}
                    onChange={(e) => setCadastroData({ ...cadastroData, nome: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="João Silva"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    data-testid="cadastro-email"
                    type="email"
                    value={cadastroData.email}
                    onChange={(e) => setCadastroData({ ...cadastroData, email: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="joao@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Senha *</label>
                  <input
                    data-testid="cadastro-senha"
                    type="password"
                    value={cadastroData.senha}
                    onChange={(e) => setCadastroData({ ...cadastroData, senha: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Telefone</label>
                  <input
                    data-testid="cadastro-telefone"
                    type="tel"
                    value={cadastroData.telefone}
                    onChange={(e) => setCadastroData({ ...cadastroData, telefone: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="(11) 98765-4321"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Data de Nascimento</label>
                  <input
                    data-testid="cadastro-nascimento"
                    type="date"
                    value={cadastroData.data_nascimento}
                    onChange={(e) => setCadastroData({ ...cadastroData, data_nascimento: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Altura (m)</label>
                  <input
                    data-testid="cadastro-altura"
                    type="number"
                    step="0.01"
                    value={cadastroData.altura}
                    onChange={(e) => setCadastroData({ ...cadastroData, altura: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="1.75"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Peso Inicial (kg)</label>
                  <input
                    data-testid="cadastro-peso-inicial"
                    type="number"
                    step="0.1"
                    value={cadastroData.peso_inicial}
                    onChange={(e) => setCadastroData({ ...cadastroData, peso_inicial: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="85.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Peso Meta (kg)</label>
                  <input
                    data-testid="cadastro-peso-meta"
                    type="number"
                    step="0.1"
                    value={cadastroData.peso_meta}
                    onChange={(e) => setCadastroData({ ...cadastroData, peso_meta: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="70.0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Duração do Tratamento (meses)</label>
                  <input
                    data-testid="cadastro-duracao"
                    type="number"
                    min="1"
                    max="60"
                    value={cadastroData.duracao_tratamento}
                    onChange={(e) => setCadastroData({ ...cadastroData, duracao_tratamento: e.target.value })}
                    className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-12 px-4"
                    placeholder="6"
                  />
                </div>
              </div>

              {/* Medidas Iniciais */}
              <div className="bg-[#B8977A]/10 rounded-xl p-4 border border-[#B8977A]/20">
                <h4 className="font-semibold text-[#B8977A] mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Medidas Iniciais (cm) - Opcional
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Cintura</label>
                    <input
                      data-testid="cadastro-cintura"
                      type="number"
                      step="0.1"
                      value={cadastroData.cintura_inicial}
                      onChange={(e) => setCadastroData({ ...cadastroData, cintura_inicial: e.target.value })}
                      className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                      placeholder="90"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Quadril</label>
                    <input
                      data-testid="cadastro-quadril"
                      type="number"
                      step="0.1"
                      value={cadastroData.quadril_inicial}
                      onChange={(e) => setCadastroData({ ...cadastroData, quadril_inicial: e.target.value })}
                      className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Peito</label>
                    <input
                      data-testid="cadastro-peito"
                      type="number"
                      step="0.1"
                      value={cadastroData.peito_inicial}
                      onChange={(e) => setCadastroData({ ...cadastroData, peito_inicial: e.target.value })}
                      className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                      placeholder="95"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Braço</label>
                    <input
                      data-testid="cadastro-braco"
                      type="number"
                      step="0.1"
                      value={cadastroData.braco_inicial}
                      onChange={(e) => setCadastroData({ ...cadastroData, braco_inicial: e.target.value })}
                      className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                      placeholder="35"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Perna</label>
                    <input
                      data-testid="cadastro-perna"
                      type="number"
                      step="0.1"
                      value={cadastroData.perna_inicial}
                      onChange={(e) => setCadastroData({ ...cadastroData, perna_inicial: e.target.value })}
                      className="w-full bg-white border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl h-10 px-3 text-sm"
                      placeholder="60"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Histórico Médico</label>
                <textarea
                  data-testid="cadastro-historico"
                  value={cadastroData.historico_medico}
                  onChange={(e) => setCadastroData({ ...cadastroData, historico_medico: e.target.value })}
                  className="w-full bg-muted border-transparent focus:border-[#B8977A] focus:ring-2 focus:ring-[#B8977A]/20 rounded-xl p-4"
                  rows={3}
                  placeholder="Alergias, condições pré-existentes, medicamentos em uso..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  data-testid="submit-cadastro"
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white rounded-xl py-3 font-semibold hover:shadow-lg hover:shadow-[#0F2B4C]/30 transition-all"
                >
                  Cadastrar Paciente
                </button>
                <button
                  type="button"
                  onClick={() => setShowCadastroForm(false)}
                  className="flex-1 bg-muted/50 text-muted-foreground rounded-xl py-3 font-semibold hover:bg-muted/70 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search Bar */}
        <div className="floating-card p-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              data-testid="search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Buscar ${selectedTab === 'patients' ? 'pacientes' : 'médicos'}...`}
              className="w-full bg-muted/50 border-transparent focus:border-[#0F2B4C] focus:ring-2 focus:ring-[#0F2B4C]/20 rounded-xl h-12 pl-12 pr-4 text-sm transition-all"
            />
          </div>
        </div>

        {/* Patients List */}
        {selectedTab === 'patients' && (
          <div className="space-y-4">
            {filteredPatients.length === 0 ? (
              <div className="floating-card text-center py-12">
                <Users className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-bold mb-2">
                  {searchTerm ? 'Nenhum paciente encontrado' : 'Nenhum Paciente Cadastrado'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {searchTerm ? 'Tente buscar com outro termo' : 'Os pacientes cadastrados aparecerão aqui'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredPatients.map((patient) => (
                  <div
                    key={patient.id}
                    data-testid={`patient-card-${patient.id}`}
                    className="floating-card p-4 sm:p-6 float-transition cursor-pointer"
                    onClick={() => navigate(`/admin/paciente/${patient.id}`)}
                  >
                    {/* Mobile Layout */}
                    <div className="sm:hidden">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#B8977A] to-[#0F2B4C] flex items-center justify-center overflow-hidden shadow-lg">
                            {patient.foto_perfil ? (
                              <img 
                                src={patient.foto_perfil} 
                                alt={patient.nome}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            )}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-highlight truncate">
                            {patient.nome}
                          </h3>
                          <p className="text-xs text-muted-foreground truncate">{patient.email}</p>
                          {patient.peso_inicial && patient.peso_meta && (
                            <p className="text-xs text-[#B8977A] font-medium mt-0.5">
                              Meta: {patient.peso_inicial} → {patient.peso_meta} kg
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Mobile Action Buttons */}
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const telefone = patient.telefone?.replace(/\D/g, '');
                            if (telefone) {
                              window.open(`https://wa.me/55${telefone}`, '_blank');
                            } else {
                              toast.error('Paciente não possui telefone cadastrado');
                            }
                          }}
                          className="p-2.5 bg-green-100 rounded-xl hover:bg-green-200 transition-colors flex-shrink-0"
                          title="WhatsApp"
                        >
                          <svg className="text-green-600" width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/paciente/${patient.id}/perfil`);
                          }}
                          className="p-2 bg-[#B8977A]/10 rounded-full hover:bg-[#B8977A]/20 transition-colors flex-shrink-0"
                          title="Perfil"
                        >
                          <Users className="text-[#B8977A]" size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPasswordModal({ isOpen: true, userId: patient.id, userName: patient.nome });
                          }}
                          className="p-2 bg-amber-100 rounded-full hover:bg-amber-200 transition-colors flex-shrink-0"
                          title="Alterar Senha"
                        >
                          <Key className="text-amber-600" size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/paciente/${patient.id}/receitas`);
                          }}
                          className="p-2 bg-[#0F2B4C]/10 rounded-full hover:bg-[#0F2B4C]/20 transition-colors flex-shrink-0"
                          title="Receitas"
                        >
                          <FileText className="text-[#0F2B4C]" size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/paciente/${patient.id}/plano`);
                          }}
                          className="p-2 bg-[#B8977A]/10 rounded-full hover:bg-[#B8977A]/20 transition-colors flex-shrink-0"
                          title="Plano"
                        >
                          <UtensilsCrossed className="text-[#B8977A]" size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFinalizarPrograma(patient.id);
                          }}
                          className="p-2 bg-green-100 rounded-full hover:bg-green-200 transition-colors flex-shrink-0"
                          title="Finalizar"
                        >
                          <svg className="text-green-600" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExcluirPaciente(patient.id, patient.nome);
                          }}
                          className="p-2 bg-red-100 rounded-full hover:bg-red-200 transition-colors flex-shrink-0"
                          title="Excluir"
                        >
                          <svg className="text-red-600" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#B8977A] to-[#0F2B4C] flex items-center justify-center overflow-hidden">
                            {patient.foto_perfil ? (
                              <img 
                                src={patient.foto_perfil} 
                                alt={patient.nome}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            )}
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-card"></div>
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-1 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {patient.nome}
                          </h3>
                          <p className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            {patient.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        {patient.peso_inicial && patient.peso_meta && (
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Meta</p>
                            <p className="text-lg font-bold text-[#B8977A]">
                              {patient.peso_inicial} → {patient.peso_meta} kg
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const telefone = patient.telefone?.replace(/\D/g, '');
                              if (telefone) {
                                window.open(`https://wa.me/55${telefone}`, '_blank');
                              } else {
                                toast.error('Paciente não possui telefone cadastrado');
                              }
                            }}
                            className="p-3 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                            title="Abrir WhatsApp"
                            data-testid={`whatsapp-${patient.id}`}
                          >
                            <svg className="text-green-600" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/paciente/${patient.id}`);
                            }}
                            className="p-3 bg-[#B8977A]/10 rounded-full hover:bg-[#B8977A]/20 transition-colors"
                            title="Ver Perfil Completo"
                            data-testid={`view-profile-${patient.id}`}
                          >
                            <Users className="text-[#B8977A]" size={20} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/paciente/${patient.id}/receitas`);
                            }}
                            className="p-3 bg-[#0F2B4C]/10 rounded-full hover:bg-[#0F2B4C]/20 transition-colors"
                            title="Gerenciar Receitas"
                            data-testid={`manage-prescriptions-${patient.id}`}
                          >
                            <FileText className="text-[#0F2B4C]" size={20} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/paciente/${patient.id}/plano`);
                            }}
                            className="p-3 bg-[#B8977A]/10 rounded-full hover:bg-[#B8977A]/20 transition-colors"
                            title="Gerenciar Plano Alimentar"
                            data-testid={`manage-mealplan-${patient.id}`}
                          >
                            <UtensilsCrossed className="text-[#B8977A]" size={20} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFinalizarPrograma(patient.id);
                            }}
                            className="p-3 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
                            title="Finalizar Programa"
                            data-testid={`finalizar-programa-${patient.id}`}
                          >
                            <svg className="text-green-600" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExcluirPaciente(patient.id, patient.nome);
                            }}
                            className="p-3 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
                            title="Excluir Paciente"
                            data-testid={`excluir-paciente-${patient.id}`}
                          >
                            <svg className="text-red-600" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPasswordModal({ isOpen: true, userId: patient.id, userName: patient.nome });
                            }}
                            className="p-3 bg-[#B8977A]/10 rounded-full hover:bg-[#B8977A]/20 transition-colors"
                            title="Alterar Senha"
                            data-testid={`change-password-${patient.id}`}
                          >
                            <Key className="text-[#B8977A]" size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Doctors List */}
        {selectedTab === 'doctors' && (
          <div className="space-y-4">
            {filteredDoctors.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto text-muted-foreground mb-4" size={64} />
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {searchTerm ? 'Nenhum médico encontrado' : 'Nenhum Médico Cadastrado'}
                </h3>
                <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {searchTerm ? 'Tente buscar com outro termo' : 'Os médicos cadastrados aparecerão aqui'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredDoctors.map((doctor) => (
                  <div
                    key={doctor.id}
                    data-testid={`doctor-card-${doctor.id}`}
                    className="bg-card rounded-2xl shadow-sm border border-border p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-[#0F2B4C]/10 flex items-center justify-center">
                        <FileText className="text-[#0F2B4C]" size={28} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-1 text-highlight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Dr(a). {doctor.nome}
                        </h3>
                        <p className="text-sm text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {doctor.email}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Alteração de Senha */}
      <ChangePasswordModal
        isOpen={passwordModal.isOpen}
        onClose={() => setPasswordModal({ isOpen: false, userId: null, userName: '' })}
        userId={passwordModal.userId}
        userName={passwordModal.userName}
      />
    </Layout>
  );
};

export default AdminDashboard;
